import axios, {
  CancelTokenSource,
  Method,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';
import { authGlobals } from '@tmapy/auth';

import type { CallApiResult } from '@tmapy/beetlejuice-utils';
import {
  HTTP_STATUS_ERROR,
  HTTP_STATUS_NO_CONTENT,
  isHttpStatusSuccess,
} from '@tmapy/beetlejuice-utils';

export const callApi = async <ResourceType>(
  url: string,
  method = 'GET' as Method,
  body?: null | undefined | FormData | object,
  onUploadProgress?: null | ((progressEvent: any) => void),
  cancelToken?: null | CancelTokenSource,
): Promise<CallApiResult<ResourceType>> => {
  const data = body instanceof (window as any).FormData ? body : JSON.stringify(body);
  const token = authGlobals.token;

  // kontrola uploadu binarnich file dat
  // pokud je to upload, tak se nepredava content-type
  let hasUploadData = false;
  if (data instanceof FormData) {
    for (const [_, value] of data.entries()) {
      if (value instanceof File) {
        hasUploadData = true;
        break;
      }
    }
  }

  const requestConfig: AxiosRequestConfig = {
    url,
    method,
    data,
    onUploadProgress: onUploadProgress ?? undefined,
    cancelToken: cancelToken ? cancelToken.token : undefined,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(!hasUploadData && { 'Content-Type': 'application/json' }),
    },
  };

  try {
    const response: AxiosResponse<ResourceType> = await axios(requestConfig);
    const { status } = response;

    // DELETE - No Content
    if (status === HTTP_STATUS_NO_CONTENT) {
      return { ok: true, status, data: response.data, error: null, fetchedAt: Date.now() };
    }

    if (isHttpStatusSuccess(status)) {
      return { ok: true, status, data: response.data, error: null, fetchedAt: Date.now() };
    }

    console.error('Bad response from server', response.statusText);
    return {
      ok: false,
      status: status as HTTP_STATUS_ERROR,
      data: null,
      error: response,
      fetchedAt: Date.now(),
    };
  } catch (error) {
    if (axios.isCancel(error)) {
      return { ok: false, status: 0, data: null, error: null, fetchedAt: Date.now() };
    }
    console.error('Api error:', error);

    const axiosError = axios.isAxiosError(error) ? (error as AxiosError) : undefined;

    return {
      ok: false,
      status: (axiosError?.response?.status as HTTP_STATUS_ERROR) || 500,
      data: null,
      error: axiosError?.response || { message: 'Network Error' },
      fetchedAt: Date.now(),
    };
  }
};
